/**
 * ⚖️ MODALS LÉGAUX - LA PETITE ÉPICERIE
 * S'ouvrent directement dans l'espace client sans quitter la page
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Lock, FileText, Scale, Mail, AlertTriangle } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// 📜 MODAL CGU
// ═══════════════════════════════════════════════════════════════════════════════

export const CGUModal = ({ isOpen, onClose, isDarkMode = true }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "1. Présentation du site",
      content: `Le présent site est édité par :

**VM MARKET** (La Petite Épicerie)
138 Rue Arthur Lamendin
62980 Vermelles, France

SIRET : 428 992 796 00022
Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76`
    },
    {
      title: "2. Acceptation des conditions",
      content: `L'utilisation du Site implique l'acceptation pleine et entière des présentes CGU. Ces conditions peuvent être modifiées à tout moment.`
    },
    {
      title: "3. Services proposés",
      content: `La Petite Épicerie propose :
• Vente de produits d'épicerie et boissons
• Programme de fidélité (cartes tampons)
• Service de livraison à domicile
• Promotions et offres spéciales`
    },
    {
      title: "4. Vente d'alcool",
      icon: <AlertTriangle className="w-4 h-4" />,
      content: `⚠️ L'ABUS D'ALCOOL EST DANGEREUX POUR LA SANTÉ

La vente d'alcool est interdite aux mineurs de moins de 18 ans. En passant commande, vous certifiez être majeur.`
    },
    {
      title: "5. Propriété intellectuelle",
      icon: <Scale className="w-4 h-4" />,
      content: `L'ensemble des éléments du Site (textes, images, logos, design) est la propriété de La Petite Épicerie. Toute reproduction non autorisée est interdite.`
    },
    {
      title: "6. Contact",
      icon: <Mail className="w-4 h-4" />,
      content: `Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76

Dernière mise à jour : Janvier 2026`
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative w-full max-w-2xl max-h-[80vh] overflow-x-hidden overflow-y-auto rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`sticky top-0 z-10 px-6 py-4 border-b ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Conditions Générales d'Utilisation
                </h2>
              </div>
              <button onClick={onClose} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-6 space-y-4">
            {sections.map((section, idx) => (
              <div key={idx} className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.icon && <span className="text-cyan-400">{section.icon}</span>}
                  {section.title}
                </h3>
                <p className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {section.content}
                </p>
              </div>
            ))}
            <div className={`text-center pt-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              © 2026 La Petite Épicerie - VM MARKET
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🔒 MODAL CONFIDENTIALITÉ
// ═══════════════════════════════════════════════════════════════════════════════

export const PrivacyModal = ({ isOpen, onClose, isDarkMode = true }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "1. Responsable du traitement",
      icon: <Shield className="w-4 h-4" />,
      content: `**VM MARKET** (La Petite Épicerie)
138 Rue Arthur Lamendin, 62980 Vermelles
Email : contact@la-petite-epicerie.shop`
    },
    {
      title: "2. Données collectées",
      content: `• Nom, prénom, email, téléphone
• Adresse de livraison
• Historique des achats
• Données de fidélité`
    },
    {
      title: "3. Vos droits RGPD",
      content: `• Droit d'accès à vos données
• Droit de rectification
• Droit à l'effacement
• Droit à la portabilité

Contact : contact@la-petite-epicerie.shop`
    },
    {
      title: "4. Sécurité",
      icon: <Lock className="w-4 h-4" />,
      content: `Vos données sont protégées par chiffrement. En cas de violation, vous serez informé sous 72h.`
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative w-full max-w-2xl max-h-[80vh] overflow-x-hidden overflow-y-auto rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`sticky top-0 z-10 px-6 py-4 border-b ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Politique de Confidentialité
                </h2>
              </div>
              <button onClick={onClose} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-6 space-y-4">
            {sections.map((section, idx) => (
              <div key={idx} className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.icon && <span className="text-green-400">{section.icon}</span>}
                  {section.title}
                </h3>
                <p className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {section.content}
                </p>
              </div>
            ))}
            <div className={`text-center pt-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              © 2026 La Petite Épicerie - Conforme RGPD
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 MODAL MENTIONS LÉGALES
// ═══════════════════════════════════════════════════════════════════════════════

export const MentionsLegalesModal = ({ isOpen, onClose, isDarkMode = true }) => {
  if (!isOpen) return null;

  const sections = [
    {
      title: "Éditeur du site",
      content: `**VM MARKET** (La Petite Épicerie)
138 Rue Arthur Lamendin
62980 Vermelles, France

SIRET : 428 992 796 00022
TVA : FR54428992796
RCS : Béthune`
    },
    {
      title: "Directeur de publication",
      content: `VM MARKET
Email : contact@la-petite-epicerie.shop
Téléphone : 07 80 64 89 76`
    },
    {
      title: "Hébergement",
      content: `Le site est hébergé par des services cloud sécurisés conformes aux normes européennes.`
    },
    {
      title: "Propriété intellectuelle",
      icon: <Scale className="w-4 h-4" />,
      content: `L'ensemble du contenu (textes, images, logos, design) est protégé par le droit de la propriété intellectuelle. Toute reproduction non autorisée constitue une contrefaçon.`
    },
    {
      title: "Médiation",
      content: `En cas de litige, vous pouvez recourir au service de médiation de la consommation.

CNIL : www.cnil.fr`
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative w-full max-w-2xl max-h-[80vh] overflow-x-hidden overflow-y-auto rounded-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`sticky top-0 z-10 px-6 py-4 border-b ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Mentions Légales
                </h2>
              </div>
              <button onClick={onClose} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-6 space-y-4">
            {sections.map((section, idx) => (
              <div key={idx} className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className={`font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.icon && <span className="text-amber-400">{section.icon}</span>}
                  {section.title}
                </h3>
                <p className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {section.content}
                </p>
              </div>
            ))}
            <div className={`text-center pt-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              © 2026 La Petite Épicerie - VM MARKET
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
